import { Activity } from '../types/ActivityTypes';
import { Project } from '../types/projectTypes';
import { ProjectStatistics } from '../types/projectStatisticsType';

/**
 * Lógica de asignación de actividades por proyecto, réplica de la hoja
 * "Proyectos - Asignacion Act." del informe SGR.
 *
 * Determina, por actividad, qué metas (P0) son automáticas y cómo se calculan a
 * partir de los objetivos de fase del proyecto (Información Obra) y los ratios de
 * conversión de las torres. Los campos no automáticos quedan manuales.
 */

export type ActivityCategory =
  | 'INDEPENDIENTE'
  | 'CAMINOS'
  | 'OBRAS_CIVILES'
  | 'MONTAJE'
  | 'TENDIDO_CONDUCTOR'
  | 'TENDIDO_OPGW';

const norm = (s?: string | null) =>
  (s || '').toString().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

/**
 * Actividades que SIEMPRE son manuales (las marcadas con "x" en el Excel),
 * aunque pertenezcan a una sección de Tendido/OPGW.
 */
const INDEPENDENT_KEYWORDS = [
  'replanteo', 'medicion de resistividad', 'deforestacion', 'excavacion',
  'acero de refuerzo', 'instalacion de sistema de pat', 'sistema de pat',
  'clasificacion de torres', 'acopio de torres', 'revisado de torres',
  'pintura', 'placas', 'acopio para el tendido', 'protecciones',
  'empalmes opgw', 'revision del tendido',
];

/**
 * Indica si el nombre de una actividad corresponde al Cable de Guarda (CG).
 *
 * No se puede buscar la subcadena 'cg' suelta porque aparecería dentro de otras palabras y daría
 * falsos positivos. Se exige que el nombre termine en ' cg', que es la convención del catálogo
 * ('Tendido CG', 'Vestido CG', 'Riega de Cable Piloto CG'...), o que contenga 'cable de guarda'
 * por si la actividad se da de alta con el nombre desarrollado.
 *
 * Debe mantenerse alineada con isGuardCableActivity de ProjectsActivitiesServiceImpl en el backend:
 * si las dos discrepan, el formulario muestra unas metas y la API persiste otras.
 *
 * @param normalizedName Nombre de la actividad ya pasado por norm().
 */
const isGuardCableActivity = (normalizedName: string): boolean =>
  normalizedName.endsWith(' cg') || normalizedName.includes('cable de guarda');

/**
 * Determina la categoría de una actividad, que decide qué metas se calculan automáticamente
 * (y por tanto se muestran read-only) y a partir de qué objetivos de fase del proyecto.
 *
 * Las reglas se evalúan en cascada y la primera que coincide gana; el orden importa. Las
 * actividades del Cable de Guarda comparten categoría con las de OPGW porque son la misma fase de
 * obra, con la misma cuadrilla y el mismo método. Sin esa regla caerían en el TENDIDO_CONDUCTOR del
 * final por tener sección 'Tendido' y tomarían los rendimientos del tendido de conductor.
 */
export const classifyActivity = (activity?: Activity): ActivityCategory => {
  const name = norm(activity?.name);
  const section = norm(activity?.section);

  if (INDEPENDENT_KEYWORDS.some((k) => name.includes(k))) return 'INDEPENDIENTE';
  if (name.includes('camino')) return 'CAMINOS';
  if (name.includes('opgw') || isGuardCableActivity(name)) return 'TENDIDO_OPGW';
  if (name.includes('nivel') || name.includes('hormigon')) return 'OBRAS_CIVILES';
  if (name.includes('pre-armado') || name.includes('pre armado') || name.includes('prearmado') || name.includes('montaje'))
    return 'MONTAJE';
  if (section === 'tendido') return 'TENDIDO_CONDUCTOR';
  return 'INDEPENDIENTE';
};

interface PhaseObjective {
  po: number;
  hh: number;
  hm: number;
  unit: 'torres' | 'km';
}

const phaseObjective = (category: ActivityCategory, project: Project): PhaseObjective | null => {
  const n = (v?: number) => Number(v || 0);
  switch (category) {
    case 'OBRAS_CIVILES':
      return { po: n(project.civilWorksPo), hh: n(project.civilWorksHh), hm: n(project.civilWorksHm), unit: 'torres' };
    case 'MONTAJE':
      return { po: n(project.assemblyPo), hh: n(project.assemblyHh), hm: n(project.assemblyHm), unit: 'torres' };
    case 'TENDIDO_CONDUCTOR':
      return { po: n(project.conductorLayingPo), hh: n(project.conductorLayingHh), hm: n(project.conductorLayingHm), unit: 'km' };
    case 'TENDIDO_OPGW':
      return { po: n(project.opgwLayingPo), hh: n(project.opgwLayingHh), hm: n(project.opgwLayingHm), unit: 'km' };
    default:
      return null;
  }
};

/** Ratio de conversión producción→avance según la unidad secundaria de la actividad. */
const advanceRatio = (activity: Activity, category: ActivityCategory, stats: ProjectStatistics | null): number => {
  if (!stats) return 0;
  const sec = norm(activity.unitOfMeasurement);
  const n = (v?: number) => Number(v || 0);

  if (category === 'CAMINOS') {
    const sub = norm(activity.subActivity) + ' ' + norm(activity.name);
    if (sub.includes('fuerte')) return n(stats.ratioMPerTowerHeavy);
    if (sub.includes('medio')) return n(stats.ratioMPerTowerMedium);
    return n(stats.ratioMPerTowerLight); // Plano / liviano por defecto
  }
  if (sec === 'm3' || sec === 'm³') return n(stats.ratioM3HormPerTower);
  if (sec === 'tn') return n(stats.ratioTnPerTower);
  if (sec === 'km') return n(stats.ratioKmPerTower);
  return 0;
};

export type GoalField =
  | 'productionGoal' | 'performacePo' | 'performaceHmPo'
  | 'performaceUnitGoal' | 'performanceUnitPo' | 'performanceUnitHmPo';

export interface ComputedGoals {
  values: Partial<Record<GoalField, number>>;
  auto: Record<GoalField, boolean>;
}

const round = (v: number, d = 2) => {
  if (!isFinite(v) || isNaN(v)) return 0;
  const f = Math.pow(10, d);
  return Math.round(v * f) / f;
};

const hasSecondaryUnit = (activity: Activity) => {
  const uom = norm(activity.unitOfMeasurement);
  const uomProd = norm(activity.unitOfMeasurementProduction) || 'torres';
  return uom !== 'na' && uom !== 'n/a' && uom !== '' && uom !== '-' && uom !== uomProd;
};

/**
 * Calcula las metas de una actividad de proyecto replicando la hoja del Excel.
 * Devuelve los valores calculados y qué campos son automáticos (para mostrarlos read-only).
 *
 * @param weightPercent Peso (Distribución del Peso) en PORCENTAJE (ej. 30 = 0,3).
 */
export const computeActivityGoals = (
  activity: Activity,
  weightPercent: number,
  project: Project,
  stats: ProjectStatistics | null,
): ComputedGoals => {
  const category = classifyActivity(activity);
  const wf = Number(weightPercent || 0) / 100; // fracción
  const objective = phaseObjective(category, project);
  const ratio = advanceRatio(activity, category, stats);
  const prodUnit = norm(activity.unitOfMeasurementProduction) || 'torres';
  const kmPerTower = Number(stats?.ratioKmPerTower || 0);

  const values: Partial<Record<GoalField, number>> = {};
  const auto: Record<GoalField, boolean> = {
    productionGoal: false, performacePo: false, performaceHmPo: false,
    performaceUnitGoal: false, performanceUnitPo: false, performanceUnitHmPo: false,
  };

  // ── Metas de producción (H/I/J) ──
  if (objective) {
    // Fase con objetivo en torres (Obras Civiles, Montaje) o en km (Tendido).
    const isKmPhaseTorres = objective.unit === 'km' && prodUnit === 'torres' && kmPerTower > 0;
    if (isKmPhaseTorres) {
      // Actividad en torres dentro de una fase medida en km: se convierte con km/torre.
      values.productionGoal = round(ratioDiv(objective.po, kmPerTower));
      values.performacePo = round(objective.hh * wf * kmPerTower);
      values.performaceHmPo = round(objective.hm * wf * kmPerTower);
    } else {
      values.productionGoal = round(objective.po);
      values.performacePo = round(objective.hh * wf);
      values.performaceHmPo = round(objective.hm * wf);
    }
    auto.productionGoal = auto.performacePo = auto.performaceHmPo = true;
  }

  // ── Metas de avance (K/L/M) — solo si hay unidad secundaria y ratio disponible ──
  if (hasSecondaryUnit(activity) && ratio > 0) {
    const baseProd = values.productionGoal;
    const baseHh = values.performacePo;
    const baseHm = values.performaceHmPo;
    // Para CAMINOS las metas de producción son manuales; el cálculo se hará en vivo
    // con los valores manuales actuales (ver computeAdvanceFromManual).
    if (category !== 'CAMINOS' && baseProd !== undefined) {
      values.performaceUnitGoal = round(baseProd * ratio);
      values.performanceUnitPo = round(ratioDiv(baseHh ?? 0, ratio));
      values.performanceUnitHmPo = round(ratioDiv(baseHm ?? 0, ratio));
      auto.performaceUnitGoal = auto.performanceUnitPo = auto.performanceUnitHmPo = true;
    } else if (category === 'CAMINOS') {
      // En caminos K/L/M son automáticos a partir de H/I/J manuales.
      auto.performaceUnitGoal = auto.performanceUnitPo = auto.performanceUnitHmPo = true;
    }
  }

  return { values, auto };
};

/** Recalcula K/L/M de CAMINOS a partir de H/I/J manuales actuales y el ratio m/torre. */
export const computeRoadAdvance = (
  activity: Activity,
  manual: { productionGoal?: number; performacePo?: number; performaceHmPo?: number },
  stats: ProjectStatistics | null,
) => {
  const ratio = advanceRatio(activity, 'CAMINOS', stats);
  if (ratio <= 0) return { performaceUnitGoal: 0, performanceUnitPo: 0, performanceUnitHmPo: 0 };
  return {
    performaceUnitGoal: round(Number(manual.productionGoal || 0) * ratio),
    performanceUnitPo: round(ratioDiv(Number(manual.performacePo || 0), ratio)),
    performanceUnitHmPo: round(ratioDiv(Number(manual.performaceHmPo || 0), ratio)),
  };
};

/** IFERROR(a/b, 0). */
const ratioDiv = (a: number, b: number) => (b && isFinite(a / b) ? a / b : 0);
